import { Injectable } from '@angular/core';
import { AngularFire } from 'angularfire2';
import { Page } from '../models';

import 'rxjs/add/operator/map';
import { FirebaseService } from './firebase.service';

@Injectable()
export class PageService extends FirebaseService {
  flatPages: Page[] = [];
  expandedFolderIds: string[] = [];

  constructor(protected _af: AngularFire) {
    super();
    this.firebaseType = 'pages';
  }

  // Called when editing a page
  getPageByUrl(firebaseUrl: string) {
    return this._af.database.object(this.firebaseRoot + '/pages' + firebaseUrl)
    .map(page => {
      page.firebaseUrl = firebaseUrl;
      return new Page(page);
    });
  }

  // Finds in-memory object by id
  findPageByID(id: string): Page {
    let page = this.flatPages.filter(page => {
      return page.id === id;
    });
    if (page.length) return page[0];
    else return null;
  }

  // Fired when the firebase objects are changed
  mapObjJson(jsonPages: any[]): Page[] {
    this.pages.length = 0;
    let pages = jsonPages.map((jsonPage) => {
      let page = new Page(jsonPage, null, this.expandedFolderIds);
      if (page.type === 'folder' && this.expandedFolderIds.indexOf(page.id) > -1) page.isCollapsed = false;
      return page;
    });

    // Updates array without breaking references -- very important -- DO NOT CHANGE!
    this.pages.length = 0;
    [].push.apply(this.pages, pages);
    this.updateFlatPages();

    return this.pages;
  }

  updateFlatPages(): Page[] {
    let flatPages: Page[] = [];

    this.pages.forEach((page) => {
      flatPages.push(page);
      flatPages = flatPages.concat(page.getFlatPages());
    });

    // Updates array without breaking references -- very important -- DO NOT CHANGE!
    this.flatPages.length = 0;
    [].push.apply(this.flatPages, flatPages);
    return this.flatPages;
  }

  expandFolder(id: string) {
    if (this.expandedFolderIds.indexOf(id) === -1) this.expandedFolderIds.push(id);
  }

  collapseFolder(id: string) {
    let index = this.expandedFolderIds.indexOf(id);
    if (index > -1) {
      this.expandedFolderIds.splice(index, 1);
    }
  }

  // Recursive function that finds a page from the in-memory
  // array using breadth-first search
  getPageObjectByUrl(firebaseUrl: string) {
    let pages = this.pages.slice();
    // Search through root pages to find the page
    for (let i = 0; i < pages.length; i++) {
      let p = pages[i].getChildByUrl(firebaseUrl);
      if (p) return p;
    };
  }

  // Get the parent page object, then iterate through each of its children and
  // update their positions in firebase.
  updatePagePositions(childUrl: string, newParentUrl: string,
                      oldParentUrl: string, newPosition: number) {
    // Make a copy of the array
    let pages = this.pages.slice();

    // EXPENSIVE. This kicks off a recursive function that breadth-first
    // searches the folder tree for a matching URL.
    // An "opportunity for improvement"
    let newParent = this.getPageObjectByUrl(newParentUrl);
    let oldParent = this.getPageObjectByUrl(oldParentUrl);
    let child = this.getPageObjectByUrl(childUrl);

    // Update their last modified date
    child.updatedAt = this.date();

    // Do the movement operation with our in-memory folders.
    if (oldParent) {
      oldParent.removeChild(child);
      oldParent.updatedAt = this.date();
    } else pages.splice(pages.indexOf(child), 1);
    if (newParent) {
      newParent.insertChild(child, newPosition);
      newParent.updatedAt = this.date();
    } else pages.splice(newPosition, 0, child);

    let firebaseList = [];
    for (let i = 0; i < pages.length; i++) {
      firebaseList.push(pages[i].toFirebaseObject());
    }
    return this._af.database.object(this.firebaseRoot).update({ pages: firebaseList });
  }

  savePage(page: any, pageUrl?: string): any {
    if (!pageUrl) { page.id = this.createID(); } // if new, it needs an id
    return this.save(page, pageUrl);
  }

  deletePagesWithKeys(keys: any) {
    let pagesToDelete = this.findPagesWithKeys(keys);
    pagesToDelete.forEach((page) => {
      this.delete(page);
    });
  }

  findPagesWithKeys(keys: any) {
    let possiblePages = this.flatPages.slice();
    let pageList: Page[] = [];
    possiblePages.forEach(page => {
      let addToList: boolean = true;
      for (let key in keys) {
        if (!page[key] || page[key] !== keys[key]) addToList = false;
      }
      if (addToList) pageList.push(page);
    });
    return pageList;
  }

  createID(): string {
    let identification: string = '';
    let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < 10; i++ ) {
        identification += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return identification;
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/services/app/content/helpers/page.service.ts